/**
 * Listings API Service
 *
 * Frontend client for /api/v1/listings. Mirrors the error-envelope shape
 * the realtor profile service uses ({success, error, errorCode}) so
 * callers can branch on `errorCode` for routing decisions and fall back
 * to `error` for human-readable copy.
 *
 * Wire shape (defined locally rather than @shared/types because the
 * backend Drizzle row has Date objects and the wire has ISO strings —
 * the shapes overlap heavily but aren't identical, so a separate DTO
 * here keeps the frontend honest about what it actually receives over
 * HTTP).
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export type PropertyType = "apartment" | "house" | "villa" | "plot";
export type TransactionType = "sale" | "rental" | "commercial";
export type ListingStatus =
	| "active"
	| "under_negotiation"
	| "sold"
	| "withdrawn";

export interface Listing {
	id: string;
	realtorId: string;
	propertyType: PropertyType;
	transactionType: TransactionType;
	bhk: number | null;
	sizeSqft: number | null;
	addressPublic: string;
	addressPrivate: string;
	city: string;
	locality: string;
	askingPrice: number;
	headline: string;
	description: string | null;
	images: string[];
	amenities: string[];
	status: ListingStatus;
	isExclusive: boolean;
	isPublic: boolean;
	publicSlug: string | null;
	createdAt: string;
	updatedAt: string;
	soldAt: string | null;
}

export type CreateListingInput = {
	propertyType: PropertyType;
	bhk?: number | null;
	sizeSqft?: number | null;
	addressPublic: string;
	addressPrivate: string;
	city: string;
	locality: string;
	askingPrice: number;
	headline: string;
	description?: string | null;
	images?: string[];
	amenities?: string[];
	isExclusive?: boolean;
};

export type UpdateListingInput = Partial<CreateListingInput>;

interface BackendErrorBody {
	message?: string;
	error?: string;
}
interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

interface ExtractedError {
	message: string;
	errorCode: string | null;
}

function extractError(error: unknown, fallback: string): ExtractedError {
	if (error == null) {
		return { message: fallback, errorCode: "UNKNOWN_ERROR" };
	}
	const err = error as AxiosLikeError;
	const body = err.response?.data;

	if (!err.response) {
		const networkMessage =
			err.code === "ECONNABORTED" || err.message?.includes("timeout")
				? "Request timed out. Check your connection and try again."
				: "Couldn't reach the server. Check your connection and try again.";
		return { message: networkMessage, errorCode: "NETWORK_ERROR" };
	}

	return {
		message: body?.message || fallback,
		errorCode: body?.error ?? null,
	};
}

interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	data?: T;
}

function buildFailure<T>(error: unknown, fallback: string): ApiResult<T> {
	const { message, errorCode } = extractError(error, fallback);
	return { success: false, error: message, errorCode };
}

// ----------------------------------------------------------------------
// READ
// ----------------------------------------------------------------------

export async function listMyListings(): Promise<ApiResult<Listing[]>> {
	try {
		const response = await apiClient.get<{
			data: { listings: Listing[] };
		}>(`${API_BASE}/listings/me`);
		return { success: true, data: response.data.data.listings };
	} catch (error: unknown) {
		return buildFailure<Listing[]>(error, "Failed to load listings");
	}
}

export async function getMyListing(
	listingId: string,
): Promise<ApiResult<Listing>> {
	try {
		const response = await apiClient.get<{
			data: { listing: Listing };
		}>(`${API_BASE}/listings/${listingId}`);
		return { success: true, data: response.data.data.listing };
	} catch (error: unknown) {
		return buildFailure<Listing>(error, "Failed to load listing");
	}
}

// ----------------------------------------------------------------------
// WRITE
// ----------------------------------------------------------------------

export async function createListing(
	input: CreateListingInput,
): Promise<ApiResult<Listing>> {
	try {
		const response = await apiClient.post<{
			data: { listing: Listing };
		}>(`${API_BASE}/listings`, input);
		return { success: true, data: response.data.data.listing };
	} catch (error: unknown) {
		return buildFailure<Listing>(error, "Failed to create listing");
	}
}

export async function updateListing(
	listingId: string,
	input: UpdateListingInput,
): Promise<ApiResult<Listing>> {
	try {
		const response = await apiClient.patch<{
			data: { listing: Listing };
		}>(`${API_BASE}/listings/${listingId}`, input);
		return { success: true, data: response.data.data.listing };
	} catch (error: unknown) {
		return buildFailure<Listing>(error, "Failed to update listing");
	}
}

export async function deleteListing(
	listingId: string,
): Promise<ApiResult<null>> {
	try {
		await apiClient.delete(`${API_BASE}/listings/${listingId}`);
		return { success: true, data: null };
	} catch (error: unknown) {
		return buildFailure<null>(error, "Failed to delete listing");
	}
}

// ----------------------------------------------------------------------
// LIFECYCLE
// ----------------------------------------------------------------------

export async function publishListing(
	listingId: string,
): Promise<ApiResult<Listing>> {
	try {
		const response = await apiClient.post<{
			data: { listing: Listing };
		}>(`${API_BASE}/listings/${listingId}/publish`);
		return { success: true, data: response.data.data.listing };
	} catch (error: unknown) {
		return buildFailure<Listing>(error, "Failed to publish listing");
	}
}

export async function unpublishListing(
	listingId: string,
): Promise<ApiResult<Listing>> {
	try {
		const response = await apiClient.post<{
			data: { listing: Listing };
		}>(`${API_BASE}/listings/${listingId}/unpublish`);
		return { success: true, data: response.data.data.listing };
	} catch (error: unknown) {
		return buildFailure<Listing>(error, "Failed to unpublish listing");
	}
}

export async function markListingSold(
	listingId: string,
): Promise<ApiResult<Listing>> {
	try {
		const response = await apiClient.post<{
			data: { listing: Listing };
		}>(`${API_BASE}/listings/${listingId}/mark-sold`);
		return { success: true, data: response.data.data.listing };
	} catch (error: unknown) {
		return buildFailure<Listing>(error, "Failed to mark listing sold");
	}
}
